'use strict';
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { ensureLiveKit } from '../loaders';
/* eslint-disable @typescript-eslint/no-explicit-any */
/**
 * HeyGen (streaming-video) renderer — ported from `HeygenLiveAvatarManager.js`
 * plus the runner's green-screen chroma-key.
 *
 * Flow: `mount()` builds a hidden `<video>` + a visible `<canvas>` and waits.
 * When the realtime session connects, `onSessionCreated()` receives the
 * server-issued LiveKit URL + token, connects to the room, attaches the avatar
 * video track to the `<video>`, and runs a per-frame chroma-key that copies the
 * video to the canvas with green pixels made transparent. The avatar's audio
 * arrives on a separate LiveKit audio track played through a `<audio>` element.
 */
export default class HeygenRenderer {
    constructor(ctx) {
        this.video = null;
        this.canvas = null;
        this.room = null;
        this.audioElements = [];
        /** Tracks already attached — see attachTrack(). */
        this.attachedTracks = new Set();
        this.chromaKeyRafId = null;
        this.connecting = false;
        this.streamReady = false;
        this.ctx = ctx;
    }
    mount() {
        return __awaiter(this, void 0, void 0, function* () {
            // Build the video/canvas scaffold; the stream connects later via
            // onSessionCreated(). Loading stays visible until the stream is ready.
            const container = this.ctx.container;
            container.innerHTML = '';
            this.video = document.createElement('video');
            this.video.autoplay = true;
            this.video.playsInline = true;
            this.video.muted = true; // avatar voice comes via the separate LiveKit audio track
            this.video.setAttribute('playsinline', 'playsinline');
            this.video.style.display = 'none';
            this.canvas = document.createElement('canvas');
            this.canvas.className = 'ss360-avatar__heygen-canvas';
            container.appendChild(this.video);
            container.appendChild(this.canvas);
        });
    }
    /** Pick up the LiveKit credentials from `session.created` and connect. */
    onSessionCreated(message) {
        const livekitUrl = message.heygenLivekitUrl;
        const clientToken = message.heygenClientToken;
        if (!livekitUrl || !clientToken) {
            this.fail('Session did not return HeyGen credentials');
            return;
        }
        void this.connect(livekitUrl, clientToken);
    }
    connect(livekitUrl, clientToken) {
        return __awaiter(this, void 0, void 0, function* () {
            if (this.connecting || this.room)
                return;
            this.connecting = true;
            try {
                const LiveKit = yield ensureLiveKit();
                const { Room, RoomEvent, Track } = LiveKit;
                const room = new Room();
                this.room = room;
                room.on(RoomEvent.TrackSubscribed, (track) => this.attachTrack(track, Track));
                room.on(RoomEvent.TrackUnsubscribed, (track) => {
                    this.attachedTracks.delete(track);
                    try {
                        track.detach();
                    }
                    catch (e) { /* ignore */ }
                });
                room.on(RoomEvent.Disconnected, () => this.fail('HeyGen stream disconnected unexpectedly'));
                yield room.connect(livekitUrl, clientToken);
                // Race safety-net: attach any tracks that were subscribed before our
                // handler was registered (mirrors HeygenLiveAvatarManager).
                for (const participant of room.remoteParticipants.values()) {
                    for (const publication of participant.trackPublications.values()) {
                        if (!publication.isSubscribed || !publication.track)
                            continue;
                        this.attachTrack(publication.track, Track);
                    }
                }
            }
            catch (err) {
                this.fail('Failed to initialize HeyGen stream: ' + (err && err.message ? err.message : String(err)));
            }
            finally {
                this.connecting = false;
            }
        });
    }
    attachTrack(track, Track) {
        // `TrackSubscribed` and the post-connect safety-net in connect() can both
        // deliver the same track. Video attach is idempotent (same element), but audio
        // is not: track.attach() with no argument mints a fresh <audio> every call, so
        // a second attach would play the avatar's voice through two sinks at once.
        if (this.attachedTracks.has(track)) {
            return;
        }
        this.attachedTracks.add(track);
        if (track.kind === Track.Kind.Video) {
            if (this.video) {
                track.attach(this.video);
                this.startChromaKey();
                if (!this.streamReady) {
                    this.streamReady = true;
                    if (this.ctx.onLoaded)
                        this.ctx.onLoaded();
                }
            }
        }
        else if (track.kind === Track.Kind.Audio) {
            // attach() with no arg creates a fresh <audio>; keep a ref to remove on destroy.
            const audioEl = track.attach();
            audioEl.autoplay = true;
            document.body.appendChild(audioEl);
            this.audioElements.push(audioEl);
        }
    }
    /** Per-frame green-screen removal: video → canvas with green → transparent. */
    startChromaKey() {
        this.stopChromaKey();
        const video = this.video;
        const canvas = this.canvas;
        if (!video || !canvas)
            return;
        const c = canvas.getContext('2d', { willReadFrequently: true, alpha: true });
        if (!c)
            return;
        const processFrame = () => {
            if (video.readyState >= video.HAVE_CURRENT_DATA && video.videoWidth > 0) {
                if (canvas.width !== video.videoWidth)
                    canvas.width = video.videoWidth;
                if (canvas.height !== video.videoHeight)
                    canvas.height = video.videoHeight;
                c.clearRect(0, 0, canvas.width, canvas.height);
                c.drawImage(video, 0, 0);
                const imageData = c.getImageData(0, 0, canvas.width, canvas.height);
                const d = imageData.data;
                for (let i = 0; i < d.length; i += 4) {
                    const r = d[i], g = d[i + 1], b = d[i + 2];
                    // Remove clearly-green pixels (chroma key).
                    if (g > 100 && g > r * 1.4 && g > b * 1.4) {
                        d[i + 3] = 0;
                    }
                }
                c.putImageData(imageData, 0, 0);
            }
            this.chromaKeyRafId = requestAnimationFrame(processFrame);
        };
        this.chromaKeyRafId = requestAnimationFrame(processFrame);
    }
    stopChromaKey() {
        if (this.chromaKeyRafId !== null) {
            cancelAnimationFrame(this.chromaKeyRafId);
            this.chromaKeyRafId = null;
        }
    }
    fail(reason) {
        console.error('[avatar] HeyGen:', reason);
        this.teardownStream();
        if (this.ctx.onError)
            this.ctx.onError({ message: reason });
    }
    // eslint-disable-next-line @typescript-eslint/no-empty-function, class-methods-use-this
    attachAudio() {
        return __awaiter(this, void 0, void 0, function* () {
            // No-op: HeyGen audio arrives on its own LiveKit track, not the playback gain node.
        });
    }
    // eslint-disable-next-line class-methods-use-this
    setMood() {
        // No-op: the HeyGen video avatar has no client-side mood morphs.
    }
    /** Retry playback of the LiveKit audio element(s) after a user gesture. */
    resumeMedia() {
        this.audioElements.forEach((el) => {
            const p = el.play();
            if (p && typeof p.catch === 'function') {
                p.catch(() => { });
            }
        });
    }
    teardownStream() {
        this.stopChromaKey();
        if (this.room) {
            try {
                this.room.disconnect();
            }
            catch (e) { /* ignore */ }
            this.room = null;
        }
        for (const el of this.audioElements) {
            try {
                el.pause();
                el.srcObject = null;
                el.remove();
            }
            catch (e) { /* ignore */ }
        }
        this.audioElements = [];
        this.attachedTracks.clear();
        this.streamReady = false;
    }
    destroy() {
        this.teardownStream();
        if (this.ctx.container) {
            this.ctx.container.innerHTML = '';
        }
        this.video = null;
        this.canvas = null;
    }
}
